import { Alignment, BoxModelConstraint, ColumnComposition, RowPanel } from '@blk/simba';
import { CellData } from '@ddt-web/data-service';
import { ScenarioHeaderConfig } from '@ddt-web/scenarios';
import * as get from 'lodash/get';

import { ReducedSpacingConfig } from '../config';
import { ScenarioHelper } from '../scenario-tester/scenario-helper';

export class TableCompositionHelper {
	private static readonly SIMPLE_TABLE_BOX_MODEL_CONSTRAINT = { paddingRight: 5, paddingTop: 5, paddingBottom: 3, useFullHeight: true };
	private static readonly SIMPLE_TABLE_CELL_BOX_MODEL_CONSTRAINT = { useFullHeight: true };
	private static readonly reducedSpacingConfig = ReducedSpacingConfig.tableCompositionHelper;

	public static getSimpleTableColumnComposition(
		isHeader: boolean, cellData: CellData, columnProportion: number,
		textClasses: string[] = ScenarioHelper.textClasses, horizontalAlignment: Alignment.Horizontal = Alignment.Horizontal.Center,
		boxModelConstraint: Partial<BoxModelConstraint> = this.SIMPLE_TABLE_BOX_MODEL_CONSTRAINT,
		cellBoxConstraint: Partial<BoxModelConstraint> = this.SIMPLE_TABLE_CELL_BOX_MODEL_CONSTRAINT
	): ColumnComposition {
		const verticalAlignment = isHeader ? Alignment.Vertical.Bottom : Alignment.Vertical.Top;
		return TableCompositionHelper.getColumnComposition(
			cellData, columnProportion, textClasses, horizontalAlignment, verticalAlignment,
			boxModelConstraint, cellBoxConstraint
		);
	}

	public static getTableHeaderColumnComposition(
		tableHeader: any[], columnProportions: Array<number>, isPrimaryHeader: boolean = true, shouldReduceSpacing: boolean = false
	): ColumnComposition[] {
		let startColumnCompositionIndex = 0;

		const tableHeaderColumnComposition: ColumnComposition[] = tableHeader.map((header: CellData, headerIndex: number) => {
			if (isPrimaryHeader) {

				// primary header with no mds scenarios
				if (header instanceof CellData) {
					return TableCompositionHelper.getNonMdsHeader(header, headerIndex, columnProportions, shouldReduceSpacing);
				} else {
					// primary header with mds scenarios
					const noOfScenarios = get(header, 'noOfScenarios', 1);
					// calculate the proportion based on the number of scenarios the mds group has
					let columnProportion = 0;
					for (let index = startColumnCompositionIndex; index < startColumnCompositionIndex + noOfScenarios; index++) {
						columnProportion += columnProportions[index];
					}
					startColumnCompositionIndex += noOfScenarios;

					return TableCompositionHelper.getMdsHeader(header, headerIndex, columnProportion, shouldReduceSpacing);
				}
			} else {
				// secondary header
				return TableCompositionHelper.getNonMdsHeader(header, headerIndex, columnProportions, shouldReduceSpacing);
			}
		});

		return tableHeaderColumnComposition;
	}

	public static getHeaderRowPanel(name: string, columnCompositions: ColumnComposition[], boxModelConstraint: Partial<BoxModelConstraint> = {}): RowPanel {
		const rowPanel = new RowPanel(
			name,
			columnCompositions.map((config: ColumnComposition) => config.renderer.render()),
			boxModelConstraint,
			Alignment.Horizontal.Left,
			Alignment.Vertical.Bottom
		);

		return rowPanel;
	}

	public static getColumnComposition(
		cell: CellData,
		widthProportion: number,
		textClasses: string[],
		horizontalAlignment: Alignment.Horizontal = Alignment.Horizontal.Center,
		verticalAlignment: Alignment.Vertical = Alignment.Vertical.Bottom,
		boxModelConstraint: Partial<BoxModelConstraint> = {},
		cellBoxModelConstraint: Partial<BoxModelConstraint> = {}
	): ColumnComposition {
		return new ColumnComposition({
			cells: [cell],
			widthProportion: widthProportion,
			textClasses: textClasses,
			horizontalAlignment: horizontalAlignment,
			verticalAlignment: verticalAlignment,
			boxModelConstraint: boxModelConstraint,
			cellBoxModelConstraint: cellBoxModelConstraint
		});
	}

	private static getDefaultBoxModelConstraint(headerIndex: number, shouldReduceSpacing: boolean): Partial<BoxModelConstraint> {
		const boxModelConstraint = shouldReduceSpacing ?
			TableCompositionHelper.reducedSpacingConfig.boxModelConstraint
			: { paddingLeft: 11, paddingRight: 11, paddingTop: 4, paddingBottom: 3, useFullHeight: true };

		if (headerIndex === 0) {
			boxModelConstraint.paddingLeft = shouldReduceSpacing ? 2 : 12;
		}

		return boxModelConstraint;
	}

	private static getDefaultCellBoxModelConstraint(headerIndex: number, border: Partial<BoxModelConstraint> = ScenarioHelper.cellBorder): Partial<BoxModelConstraint> {
		return headerIndex === 0 ? { useFullHeight: true } : { ...border, useFullHeight: true };
	}

	private static getNonMdsHeader(header: CellData, headerIndex: number, columnProportions: Array<number>, shouldReduceSpacing: boolean): ColumnComposition {
		const horizontalAlignment = ScenarioHelper.getHorizontalAlignmentBasedOnIndex(headerIndex, false);
		const boxModelConstraint = TableCompositionHelper.getDefaultBoxModelConstraint(headerIndex, shouldReduceSpacing);
		const cellBoxModelConstraint = TableCompositionHelper.getDefaultCellBoxModelConstraint(headerIndex);

		return TableCompositionHelper.getColumnComposition(
			header, columnProportions[headerIndex], ScenarioHelper.textClasses, horizontalAlignment, Alignment.Vertical.Bottom, boxModelConstraint, cellBoxModelConstraint
		);
	}

	private static getMdsHeader(header: ScenarioHeaderConfig, headerIndex: number, columnProportion: number, shouldReduceSpacing: boolean): ColumnComposition {
		const horizontalAlignment = ScenarioHelper.getHorizontalAlignmentBasedOnIndex(headerIndex, true);

		// create CellData with groupName as value to render
		const groupName = get(header, 'name');
		const cell = new CellData(null, groupName, groupName);

		// set textClasses to text-white if header is not mds (currently need to have a value to show panel with borders)
		let textClasses = ScenarioHelper.textClasses;
		const isHeaderWithValidMdsScenarioName = groupName !== '-'; // if header is not mds, groupName is set to '-'
		if (!isHeaderWithValidMdsScenarioName) {
			textClasses = ['text-white'];
		}

		const border = ScenarioHelper.getCellBorder(isHeaderWithValidMdsScenarioName);
		const boxModelConstraint = TableCompositionHelper.getDefaultBoxModelConstraint(headerIndex, shouldReduceSpacing);
		const cellBoxModelConstraint = TableCompositionHelper.getDefaultCellBoxModelConstraint(headerIndex, border);

		return TableCompositionHelper.getColumnComposition(
			cell, columnProportion, textClasses, horizontalAlignment, Alignment.Vertical.Bottom, boxModelConstraint, cellBoxModelConstraint
		);
	}

	public static getTableColumnBoxModelConstraints(index: number, shouldReduceSpacing: boolean = false): number {
		let reduceSpacingConstraint: number;
		if (shouldReduceSpacing) {
			reduceSpacingConstraint = TableCompositionHelper.reducedSpacingConfig.tableColumnBoxModelConstraints;
		}
		switch (index) {
			case 1:
				return reduceSpacingConstraint || 145;
			case 2:
				return reduceSpacingConstraint || 63;
			case 3:
			case 4:
			case 5:
				return reduceSpacingConstraint || 35;
			case 6:
				return reduceSpacingConstraint || 10;
			case 7:
			case 8:
			case 9:
			case 10:
			case 11:
			case 12:
			case 13:
				return reduceSpacingConstraint || 5;
			case 14:
				return reduceSpacingConstraint || 3;
			case 15:
				return reduceSpacingConstraint || 0;
		}
	}

	public static getHoldingsColumnProportions(portfolioCount: number, headerRow: boolean = false, shouldReduceSpacing: boolean = false): Array<number> {
		let reduceSpacingHeaderProportions: Array<number>;
		let reduceSpacingBodyProportions: Array<number>;
		if (shouldReduceSpacing) {
			reduceSpacingHeaderProportions = TableCompositionHelper.reducedSpacingConfig.holdingsColumnProportionsHeaderRow[portfolioCount];
			reduceSpacingBodyProportions = TableCompositionHelper.reducedSpacingConfig.holdingsColumnProportionsBodyRow[portfolioCount];
		}
		if (headerRow) {
			switch (portfolioCount) {
				case 1:
					return [40, 60];
				case 2:
					return [40, 30, 30];
				case 3:
					return [40, 20, 20, 20];
				case 4:
					return [40, 15, 15, 15, 15];
				case 5:
					return reduceSpacingHeaderProportions || [25, 15, 15, 15, 15, 15];
				case 6:
					return reduceSpacingHeaderProportions || [28, 12, 12, 12, 12, 12, 12];
				case 7:
					return reduceSpacingHeaderProportions || [30, 10, 10, 10, 10, 10, 10, 10];
				case 8:
					return reduceSpacingHeaderProportions || [28, 9, 9, 9, 9, 9, 9, 9, 9];
				case 9:
					return reduceSpacingHeaderProportions || [28, 8, 8, 8, 8, 8, 8, 8, 8, 8];
				case 10:
					return reduceSpacingHeaderProportions || [24, 7.6, 7.6, 7.6, 7.6, 7.6, 7.6, 7.6, 7.6, 7.6, 7.6];
				case 11:
					return reduceSpacingHeaderProportions || [23, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7];
				case 12:
					return reduceSpacingHeaderProportions || [22, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5];
				case 13:
					return reduceSpacingHeaderProportions || [22, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6];
				case 14:
					return reduceSpacingHeaderProportions || [21.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6];
				case 15:
					return reduceSpacingHeaderProportions || [19, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4];

			}
		} else {
			switch (portfolioCount) {
				case 1:
					return [10, 30, 60];
				case 2:
					return [10, 30, 30, 30];
				case 3:
					return [10, 30, 20, 20, 20];
				case 4:
					return [10, 30, 15, 15, 15, 15];
				case 5:
					return reduceSpacingBodyProportions || [13, 12, 15, 15, 15, 15, 15];
				case 6:
					return reduceSpacingBodyProportions || [13, 15, 12, 12, 12, 12, 12, 12];
				case 7:
					return reduceSpacingBodyProportions || [13, 17, 10, 10, 10, 10, 10, 10, 10];
				case 8:
					return reduceSpacingBodyProportions || [13, 15, 9, 9, 9, 9, 9, 9, 9, 9];
				case 9:
					return reduceSpacingBodyProportions || [10, 18, 8, 8, 8, 8, 8, 8, 8, 8, 8];
				case 10:
					return reduceSpacingBodyProportions || [8, 16, 7.6, 7.6, 7.6, 7.6, 7.6, 7.6, 7.6, 7.6, 7.6, 7.6];
				case 11:
					return reduceSpacingBodyProportions || [8, 15, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7, 7];
				case 12:
					return reduceSpacingBodyProportions || [8, 14, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5, 6.5];
				case 13:
					return reduceSpacingBodyProportions || [8, 14, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6, 6];
				case 14:
					return reduceSpacingBodyProportions || [8, 13.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6, 5.6];
				case 15:
					return reduceSpacingBodyProportions || [7, 12, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4, 5.4];
			}
		}
	}

	public static getAsOfDateColumnProportions(portfolioCount: number): Array<number> {

		switch (portfolioCount) {
			case 1:
				return [60];
			case 2:
				return [30, 30];
			case 3:
				return [20, 20, 20];
			case 4:
				return [15, 15, 15, 15];
		}
	}
}
